import { type FormEvent, useEffect, useState } from 'react';
import { i18n, wordCount } from '@wms/shared';
import { api } from '../lib/api.js';
import { Dialog } from './Dialog.js';
import { NumberInput } from './NumberInput.js';
import { SearchSelect } from './SearchSelect.js';

const { t } = i18n;

interface SkuOpt {
  id: string;
  code: string;
  type: 'PRODUCT' | 'COMPONENT';
  nameLt: string;
  photoUrl: string | null;
  availableQty: number;
  maxProducibleQty: number | null;
}

interface InventoryPallet {
  id: string;
  code: string;
  contents: { skuId: string; remainingQty: number }[];
}
interface DashboardRow {
  skuId: string;
  partnerId: string;
  onHand: number;
  stockPallets: { qty: number }[];
}

type AdjustmentMode = '' | 'in' | 'out' | 'actual';

interface AdjustmentLine {
  skuId: string;
  mode: AdjustmentMode;
  qty: number;
  location: 'SHELF' | 'PALLET';
  palletId: string;
  counts?: { palletId: string; current: number; qty: number | null }[];
}

export function AdjustStockDialog({
  partnerId,
  onClose,
  onRecorded,
}: {
  partnerId: string;
  onClose: () => void;
  onRecorded: () => void;
}) {
  const [skus, setSkus] = useState<SkuOpt[]>([]);
  const [shelfStock, setShelfStock] = useState<Record<string, number>>({});
  const [pallets, setPallets] = useState<InventoryPallet[]>([]);
  const [lines, setLines] = useState<AdjustmentLine[]>([]);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    Promise.all([
      api<SkuOpt[]>(`/partners/${partnerId}/skus`),
      api<InventoryPallet[]>(`/partners/${partnerId}/stock/receipt-pallets`),
      api<{ components: DashboardRow[]; products: DashboardRow[] }>('/stock/dashboard'),
    ])
      .then(([rows, eligiblePallets, dashboard]) => {
        setSkus(rows);
        setPallets(eligiblePallets);
        setShelfStock(
          Object.fromEntries(
            [...dashboard.components, ...dashboard.products]
              .filter((row) => row.partnerId === partnerId)
              .map((row) => [
                row.skuId,
                row.onHand - row.stockPallets.reduce((sum, pallet) => sum + pallet.qty, 0),
              ]),
          ),
        );
        if (rows.length > 0) {
          setLines([{ skuId: '', mode: '', qty: 0, location: 'SHELF', palletId: '' }]);
        }
      })
      .catch(() => {
        setSkus([]);
        setLines([]);
      });
  }, [partnerId]);

  function addLine() {
    if (lines.some((line) => !line.skuId)) return;
    setLines((draft) => [
      ...draft,
      { skuId: '', mode: '', qty: 0, location: 'SHELF', palletId: '' },
    ]);
  }

  function removeLine(index: number) {
    setLines((draft) => draft.filter((_, draftIndex) => draftIndex !== index));
  }

  function patchLine(index: number, patch: Partial<AdjustmentLine>) {
    setLines((draft) =>
      draft.map((line, draftIndex) => {
        if (draftIndex !== index) return line;
        const next = { ...line, ...patch };
        if (patch.skuId !== undefined || patch.mode !== undefined) {
          next.counts =
            next.mode === 'actual' && next.skuId
              ? [
                  {
                    palletId: '',
                    current: shelfStock[next.skuId] ?? 0,
                    qty: shelfStock[next.skuId] ?? 0,
                  },
                  ...pallets.flatMap((pallet) => {
                    const content = pallet.contents.find((item) => item.skuId === next.skuId);
                    return content
                      ? [
                          {
                            palletId: pallet.id,
                            current: content.remainingQty,
                            qty: content.remainingQty,
                          },
                        ]
                      : [];
                  }),
                ]
              : undefined;
        }
        return next;
      }),
    );
  }

  const selectedSkuIds = lines.map((line) => line.skuId).filter(Boolean);
  const hasDuplicates = new Set(selectedSkuIds).size !== selectedSkuIds.length;
  const reasonValid = wordCount(reason) >= 2;
  const linesValid =
    lines.length > 0 &&
    lines.every(
      (line) =>
        line.skuId &&
        line.mode &&
        (line.mode === 'actual'
          ? Boolean(line.counts?.length) &&
            line.counts!.every(
              (count) =>
                count.qty !== null &&
                Number.isInteger(count.qty) &&
                count.qty >= 0 &&
                count.qty <= 1_000_000,
            )
          : (line.location !== 'PALLET' || Boolean(line.palletId)) &&
            Number.isInteger(line.qty) &&
            line.qty > 0),
    );
  const locationCount = lines.reduce(
    (sum, line) => sum + (line.mode === 'actual' ? (line.counts?.length ?? 0) : 1),
    0,
  );
  const valid = linesValid && reasonValid && !hasDuplicates && locationCount <= 200;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!reasonValid) {
      setError(t('stock.adjust.reason_too_short'));
      return;
    }
    if (!valid) {
      setError(t('stock.adjust.invalid_lines'));
      return;
    }
    setBusy(true);
    try {
      await api(`/partners/${partnerId}/stock/inventory`, {
        method: 'POST',
        body: {
          lines: lines.flatMap((line) =>
            line.mode === 'actual'
              ? line.counts!.map((count) => ({
                  skuId: line.skuId,
                  mode: 'actual',
                  qty: count.qty,
                  location: count.palletId
                    ? { type: 'PALLET', palletId: count.palletId }
                    : { type: 'SHELF' },
                }))
              : [
                  {
                    skuId: line.skuId,
                    mode: line.mode,
                    qty: line.qty,
                    location:
                      line.location === 'PALLET'
                        ? { type: 'PALLET', palletId: line.palletId }
                        : { type: 'SHELF' },
                  },
                ],
          ),
          reason,
        },
      });
      onRecorded();
    } catch {
      setError(t('common.error'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog title={t('stock.adjust.title')} onClose={onClose}>
      <form
        onSubmit={(e) => void onSubmit(e)}
        className="space-y-3 text-sm max-h-[70vh] overflow-y-auto pr-1"
      >
        {skus.length === 0 ? (
          <div className="rounded border border-dashed border-border p-3 text-center text-xs text-muted-foreground">
            {t('stock.adjust.empty')}
          </div>
        ) : (
          <>
            <ul className="space-y-2">
              {lines.map((line, index) => {
                const unavailable = new Set(
                  lines
                    .filter((_, otherIndex) => otherIndex !== index)
                    .map((otherLine) => otherLine.skuId)
                    .filter(Boolean),
                );
                return (
                  <li key={index} className="space-y-2 rounded border border-border p-2">
                    <div className="flex items-start gap-2">
                      <label className="min-w-0 flex-1 space-y-1">
                        <span className="font-medium">{t('stock.col.sku')}</span>
                        <SearchSelect
                          className="w-full text-sm"
                          ariaLabel={t('stock.col.sku')}
                          value={line.skuId}
                          onChange={(skuId) => patchLine(index, { skuId })}
                          options={skus
                            .filter((sku) => !unavailable.has(sku.id))
                            .map((sku) => ({
                              id: sku.id,
                              label: `${sku.code} — ${sku.nameLt} (${t(`sku.type.${sku.type}`)})`,
                              imageUrl: sku.photoUrl,
                              availableQty: sku.availableQty,
                              maxProducibleQty: sku.maxProducibleQty ?? undefined,
                            }))}
                        />
                      </label>
                      <button
                        type="button"
                        onClick={() => removeLine(index)}
                        disabled={lines.length <= 1}
                        aria-label={t('common.delete')}
                        className="mt-6 rounded border border-border px-2 py-1.5 hover:bg-muted disabled:opacity-40"
                      >
                        ✕
                      </button>
                    </div>
                    <div className="grid gap-2 sm:grid-cols-2 sm:items-end">
                      {line.mode !== 'actual' ? (
                        <label className="space-y-1">
                          <span className="font-medium">{t('stock.adjust.location')}</span>
                          <select
                            value={line.location}
                            onChange={(event) =>
                              patchLine(index, {
                                location: event.target.value as AdjustmentLine['location'],
                                palletId: '',
                              })
                            }
                            className="w-full rounded border border-border bg-background px-2 py-1.5"
                          >
                            <option value="SHELF">{t('nav.shelf')}</option>
                            <option value="PALLET">{t('nav.pallets')}</option>
                          </select>
                        </label>
                      ) : null}
                      {line.mode !== 'actual' && line.location === 'PALLET' ? (
                        <label className="space-y-1">
                          <span className="font-medium">{t('stock.adjust.pallet')}</span>
                          <select
                            value={line.palletId}
                            onChange={(event) => patchLine(index, { palletId: event.target.value })}
                            required
                            className="w-full rounded border border-border bg-background px-2 py-1.5"
                          >
                            <option value="">{t('common.select')}</option>
                            {pallets.map((pallet) => (
                              <option key={pallet.id} value={pallet.id}>
                                {pallet.code}
                              </option>
                            ))}
                          </select>
                        </label>
                      ) : null}
                      <label className="space-y-1">
                        <span className="font-medium">{t('stock.adjust.direction')}</span>
                        <select
                          value={line.mode}
                          onChange={(e) =>
                            patchLine(index, { mode: e.target.value as AdjustmentMode })
                          }
                          required
                          className="w-full rounded border border-border bg-background px-2 py-1.5"
                        >
                          <option value="">{t('stock.adjust.choose')}</option>
                          <option value="in">+ {t('stock.adjust.direction_in')}</option>
                          <option value="out">− {t('stock.adjust.direction_out')}</option>
                          <option value="actual">{t('stock.adjust.actual')}</option>
                        </select>
                      </label>
                      {line.mode !== 'actual' ? (
                        <label className="space-y-1">
                          <span className="font-medium">{t('stock.col.qty')}</span>
                          <NumberInput
                            integer
                            min={0}
                            value={line.qty}
                            onChange={(qty) => patchLine(index, { qty: qty ?? 0 })}
                            required
                            className="w-full rounded border border-border bg-background px-2 py-1.5 tabular-nums"
                          />
                        </label>
                      ) : null}
                      {line.mode === 'actual' ? (
                        <p className="sm:col-span-2 text-xs text-muted-foreground">
                          {t('stock.adjust.location_counts_help')}
                        </p>
                      ) : null}
                      {line.mode === 'actual' ? (
                        line.counts?.map((count, countIndex) => {
                          const label = count.palletId
                            ? pallets.find((pallet) => pallet.id === count.palletId)!.code
                            : t('nav.shelf');
                          return (
                            <label key={count.palletId || 'SHELF'} className="space-y-1">
                              <span className="font-medium">
                                {t('stock.adjust.actual_qty')} — {label}
                              </span>
                              <NumberInput
                                integer
                                min={0}
                                max={1_000_000}
                                value={count.qty}
                                required
                                aria-label={`${t('stock.adjust.actual_qty')} — ${label}`}
                                onChange={(qty) =>
                                  patchLine(index, {
                                    counts: line.counts!.map((item, itemIndex) =>
                                      itemIndex === countIndex ? { ...item, qty } : item,
                                    ),
                                  })
                                }
                                className="w-full rounded border border-border bg-background px-2 py-1.5 tabular-nums"
                              />
                              <span className="block text-xs text-muted-foreground">
                                {t('stock.adjust.current_at_location')}: {count.current}
                              </span>
                            </label>
                          );
                        })
                      ) : (
                        <div className="pb-1.5 text-xs text-muted-foreground whitespace-nowrap">
                          {t('stock.adjust.current_at_location')}:{' '}
                          {line.location === 'PALLET'
                            ? (pallets
                                .find((pallet) => pallet.id === line.palletId)
                                ?.contents.find((content) => content.skuId === line.skuId)
                                ?.remainingQty ?? 0)
                            : (shelfStock[line.skuId] ?? 0)}
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
            <button
              type="button"
              onClick={addLine}
              disabled={lines.length >= skus.length || lines.some((line) => !line.skuId)}
              className="rounded border border-border px-3 py-1.5 hover:bg-muted disabled:opacity-40"
            >
              + {t('stock.adjust.add_line')}
            </button>
          </>
        )}

        <label className="block space-y-1">
          <span className="font-medium">{t('stock.adjust.reason')}</span>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            required
            rows={2}
            placeholder={t('stock.adjust.reason_hint')}
            className="w-full rounded border border-border bg-background px-2 py-1.5"
          />
          <span className="block text-xs text-muted-foreground">
            {t('stock.adjust.reason_help')}
          </span>
        </label>

        {error ? <div className="text-red-600">{error}</div> : null}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-border px-3 py-1.5"
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            disabled={busy || !valid}
            className="rounded bg-primary text-primary-foreground px-3 py-1.5 disabled:opacity-50"
          >
            {busy ? t('common.loading') : t('stock.adjust.submit')}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
